import { Form, Formik, type FormikProps } from 'formik';
import Button from '@common/components/controls/Button';
import * as formConfig from '@common/components/FeedbackPopUp/formConfig';
import { type FormValues } from '@common/components/FeedbackPopUp/formConfig';
import GeneralError from '@common/components/FeedbackPopUp/GeneralError';
import FileUploadField from '@common/components/form/FileUploadField';
import InputField from '@common/components/form/InputField';
import TextareaField from '@common/components/form/TextareaField';
import Icon from '@common/components/Icon';
import PopUp from '@common/components/PopUp';
import IconFeedbackSent from '@common/components/svgs/common/FeedbackSent';
import Translated from '@common/components/Translated';
import useAccessRights from '@common/hooks/useAccessRights';
import useDemoStatus from '@common/hooks/useDemoStatus';
import '@common/styles/components/feedbackPopUp.pcss';

interface Props {
  hide: () => void;
}

function FeedbackPopUp({ hide }: Readonly<Props>) {
  const isDemoMode = useDemoStatus();
  const { isCustomer } = useAccessRights();

  return (
    <Formik
      initialValues={formConfig.initialValues}
      onSubmit={formConfig.submit}
      validate={(values: FormValues) => formConfig.validate(values, isCustomer)}
    >
      {({
        isSubmitting,
        status: { success, error } = {},
        values,
        submitCount,
      }: FormikProps<formConfig.FormValues>) => (
        <Form>
          <PopUp
            title={<Translated id="feedback.popup.title" />}
            close={hide}
            noCancelButton={isSubmitting || success}
            type="narrow"
            actionButton={success
              ? (
                <Button type="button" onClick={hide} data-testid="feedbackDoneButton">
                  <Translated id="common.actions.done" />
                </Button>
              )
              : (
                <Button
                  type="submit"
                  forbidden={isDemoMode}
                  loadingState={isSubmitting ? 'loading' : undefined}
                  data-testid="feedbackSubmitButton"
                >
                  <Translated id="common.actions.send" />
                </Button>
              )}
          >
            <div className="feedback-popup">
              {success
                ? (
                  <div className="feedback-popup__success">
                    <Icon component={IconFeedbackSent} />
                    <Translated id="feedback.popup.success.title" tagName="h4" />
                    <Translated id="feedback.popup.success.subtitle" tagName="p" />
                  </div>
                )
                : (
                  <>
                    <p className="feedback-popup__description">
                      <Translated id="feedback.popup.subtitle.brandName" />
                    </p>
                    <GeneralError submitCount={submitCount} error={error} />
                    <InputField
                      name="topic"
                      placeholder="feedback.popup.fields.topic"
                      disabled={isSubmitting}
                      showError={!!values.topic}
                      data-testid="feedbackTopicInput"
                      fullWidth
                    />
                    <TextareaField
                      name="description"
                      placeholder="feedback.popup.fields.description"
                      disabled={isSubmitting}
                      showError={!!values.description}
                      data-testid="feedbackDescriptionInput"
                    />
                    <FileUploadField
                      name="uploadedFiles"
                      size={formConfig.maximumFileSize}
                      accept={formConfig.getAllowedFileExtensions(isCustomer)}
                      loading={isSubmitting}
                      disabled={isSubmitting}
                      multiple
                    />
                  </>
                )}
            </div>
          </PopUp>
        </Form>
      )}
    </Formik>
  );
}

export default FeedbackPopUp;
